import { CameraPhotoVehicles } from '@EcamModel/model';
import NoDataIcon from '@assets/jsx-icon/NoDataIcon';
import BaseCheckbox from '@components/BaseCheckbox';
import { appConfig } from '@configs/index';
import { pcController } from '@controllers/index';
import { LoadingButton } from '@mui/lab';
import {
    Box,
    Button,
    CircularProgress,
    FormControlLabel,
    Stack,
    SxProps,
    TextField,
    Theme,
    Typography,
    styled,
} from '@mui/material';
import { useImageDimensions } from '@pages/cameras/live-photos/components/PreviewImage';
import color from '@theme/Colors';
import { Dispatch, SetStateAction, useCallback, useContext, useEffect, useState } from 'react';
import { Image as KonvaImage, Layer, Rect, Stage } from 'react-konva';
import { adjustPreviewSize } from 'src/helpers';
import { PCInfo } from '..';
import MyModal from '../hooks/MyModal';
import { PotentialContraventionContext } from '../hooks/PotentialContraventionProvider';
import { KPopup } from '../hooks/useModal';

type Props = KPopup & {
    selectedPc: PCInfo;
    popupChangePlace: {
        open: boolean;
        setOpen: Dispatch<SetStateAction<boolean>>;
        handleOpen: () => void;
        handleClose: () => void;
    };
    updatingPlate?: boolean;
};

export default function ModalChangePlate(props: Props) {
    const { selectedPc } = props;

    const pcContext = useContext(PotentialContraventionContext);

    const {
        setIsLoadingLookUp,
        setIsShowLookup,
        setIsShowForeignPlate,
        isLoadingLookUp,
        isShowLookup,
        isShowForeignPlate,
        isForeignPlate,
        setIsForeignPlate,
        vehicleLookup,
        setVehicleLookup,
        vrn,
        setVrn,
        handleInputChange,
        handleUpdateChange,
        groupPc,
    } = pcContext;

    const [collapseNotifications, setCollapseNotifications] = useState<boolean>(true);

    const handleClose = () => {
        // setVrn('');
        setIsLoadingLookUp(false);
        setIsShowLookup(false);
        setIsShowForeignPlate(false);
        props.handleClose();
    };

    const fetchVRN = () => {
        setIsLoadingLookUp(true);
        setIsShowLookup(true);
        pcController
            .lookUp(vrn!, { GroupPotentialContraventionId: groupPc?.Id! })
            .then((res) => {
                setVehicleLookup(res);
            })
            .catch((err) => {
                setIsShowForeignPlate(true);
            })
            .finally(() => {
                setIsLoadingLookUp(false);
            });
    };

    const handleKeyDown = useCallback(
        (e: KeyboardEvent) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                e.stopImmediatePropagation();
                if (!isShowLookup) {
                    if (vrn) fetchVRN();
                } else {
                    if (!isLoadingLookUp && !props.updatingPlate) handleUpdateChange();
                }
            }
        },
        [isShowLookup, vrn, isLoadingLookUp, props.updatingPlate]
    );

    useEffect(() => {
        if (!props.open) return;
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [props.open, handleKeyDown]);

    useEffect(() => {
        if (!props.open) return;
        setVrn(props.selectedPc.Plate);
        setCollapseNotifications(true);
    }, [props.selectedPc.Plate, props.open]);

    const VehicleLookupData = () => {
        return (
            <Stack
                direction={'column'}
                justifyContent={'center'}
                width={'100%'}
                sx={{ '& .MuiTypography-root': { textAlign: 'center' } }}
                gap={1}
            >
                <Box
                    sx={{
                        width: '100%',
                        height: { xs: '200px', sm: '250px', md: '300px' },
                        maxHeight: '350px',
                        background: `url('${vehicleLookup.BlobName ?? '/assets/images/CarNoImage.png'}')`,
                        backgroundRepeat: 'no-repeat',
                        backgroundSize: 'contain',
                        backgroundPosition: 'center',
                    }}
                ></Box>
                <Stack direction={'row'} justifyContent={'space-evenly'}>
                    <Typography>Make: {vehicleLookup.Make}</Typography>
                    <Typography>Model: {vehicleLookup.Model}</Typography>
                    <Typography>Color: {vehicleLookup.Color}</Typography>
                    <Typography>Fuel Type: {vehicleLookup.FuelType}</Typography>
                </Stack>
            </Stack>
        );
    };

    const isForeignPlaceValid = isShowForeignPlate && !isForeignPlate;

    const ForeignPlate = () => {
        return (
            <Stack
                direction={'column'}
                justifyContent={'center'}
                alignItems={'center'}
                width={'100%'}
                sx={{ '& .MuiTypography-root': { textAlign: 'center' } }}
                gap={1}
            >
                <NoDataIcon />
                <Typography>No Data</Typography>
                <Typography>The vehicle information could not be found.</Typography>
                <FormControlLabel
                    sx={{ '&.MuiFormControlLabel-root': { margin: 0 } }}
                    checked={isForeignPlate}
                    control={
                        <BaseCheckbox
                            onChange={(_, checked) => {
                                setIsForeignPlate(checked);
                            }}
                        />
                    }
                    label={
                        <Typography ml={0.5} mt={0.3} sx={{ color: color.priText }}>
                            Please tick here if your vehicle registration is a foreign license plate.
                        </Typography>
                    }
                />
            </Stack>
        );
    };

    const entryPhoto = selectedPc.EntryPhotos?.length
        ? selectedPc.EntryPhotos.reduce((max, item) => ((item.Id ?? 0) > (max.Id ?? 0) ? item : max))
        : undefined;
    const validationPhoto = selectedPc.ValidationPhotos?.length
        ? selectedPc.ValidationPhotos.reduce((max, item) => ((item.Id ?? 0) > (max.Id ?? 0) ? item : max))
        : undefined;
    const exitPhoto = selectedPc.ExitPhotos?.length
        ? selectedPc.ExitPhotos.reduce((max, item) => ((item.Id ?? 0) > (max.Id ?? 0) ? item : max))
        : undefined;
    const overviewPhoto = selectedPc.OverviewPhotos?.length
        ? selectedPc.OverviewPhotos.reduce((max, item) => ((item.Id ?? 0) > (max.Id ?? 0) ? item : max))
        : undefined;

    const entryBlobName = entryPhoto?.BlobName;
    const validationBlobName = validationPhoto?.BlobName;
    const exitBlobName = exitPhoto?.BlobName;
    const overviewBlobNamePhoto = overviewPhoto?.BlobName;

    return (
        <MyModal
            {...props}
            handleClose={() => handleClose()}
            width={1000}
            modalContent={
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: 'auto',
                        maxHeight: '90vh',
                        width: '100%',
                        bgcolor: color.white,
                        borderRadius: '10px',
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        sx={{
                            flexShrink: 0,
                            p: 3,
                            borderBottom: `1px solid ${color.grey300}`,
                            bgcolor: color.white,
                        }}
                    >
                        <Typography variant="h4" textAlign={'center'}>
                            Change plate
                        </Typography>
                        <Typography textAlign={'center'}>
                            Please provide the license plate number that you wish to change in the box below.
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            flex: 1,
                            overflow: 'auto',
                            p: 3,
                            minHeight: 0,
                        }}
                    >
                        <Stack direction={'column'} spacing={2}>
                            <CustomTextField
                                value={vrn}
                                onChange={handleInputChange}
                                placeholder={'Enter Reg'}
                                autoFocus
                                disabled={isLoadingLookUp}
                                inputProps={{
                                    maxLength: 20,
                                }}
                            />

                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    flexWrap: 'nowrap',
                                    overflowX: 'auto',
                                    gap: 2,
                                    maxWidth: '100%',
                                    alignItems: 'center',
                                    pb: 1,
                                    justifyContent: 'center',
                                }}
                            >
                                {entryBlobName ? (
                                    <Box
                                        sx={{
                                            width: overviewPhoto ? 180 : 200,
                                            height: 115,
                                            background: `url(${appConfig.gateway.blobURL}${entryBlobName}) no-repeat center center`,
                                            backgroundSize: 'cover',
                                            borderRadius: 1,
                                            cursor: 'pointer',
                                        }}
                                    />
                                ) : (
                                    <CropPlate key={entryPhoto?.CameraPhoto?.BlobName} photo={entryPhoto!} />
                                )}

                                {validationBlobName ? (
                                    <Box
                                        sx={{
                                            width: overviewPhoto ? 180 : 200,
                                            height: 115,
                                            background: `url(${appConfig.gateway.blobURL}${validationBlobName}) no-repeat center center`,
                                            backgroundSize: 'cover',
                                            borderRadius: 1,
                                            cursor: 'pointer',
                                        }}
                                    />
                                ) : (
                                    <CropPlate key={validationPhoto?.CameraPhoto?.BlobName} photo={validationPhoto!} />
                                )}

                                {exitBlobName ? (
                                    <Box
                                        sx={{
                                            width: overviewPhoto ? 180 : 200,
                                            height: 115,
                                            background: `url(${appConfig.gateway.blobURL}${exitBlobName}) no-repeat center center`,
                                            backgroundSize: 'cover',
                                            borderRadius: 1,
                                            cursor: 'pointer',
                                        }}
                                    />
                                ) : (
                                    <CropPlate key={exitPhoto?.CameraPhoto?.BlobName} photo={exitPhoto!} />
                                )}

                                {overviewPhoto &&
                                    (overviewBlobNamePhoto ? (
                                        <Box
                                            sx={{
                                                width: 180,
                                                height: 115,
                                                background: `url(${appConfig.gateway.blobURL}${overviewBlobNamePhoto}) no-repeat center center`,
                                                backgroundSize: 'cover',
                                                borderRadius: 1,
                                                cursor: 'pointer',
                                            }}
                                        />
                                    ) : (
                                        <CropPlate
                                            key={overviewPhoto?.CameraPhoto?.BlobName}
                                            photo={overviewPhoto!}
                                            isOverviewPhoto
                                        />
                                    ))}
                            </Box>

                            {isShowLookup && (
                                <Stack sx={{ border: `1px solid ${color.grey300}`, borderRadius: '4px', p: 2 }}>
                                    {isLoadingLookUp ? (
                                        <Box sx={{ display: 'flex' }} justifyContent={'center'}>
                                            <CircularProgress />
                                        </Box>
                                    ) : isShowForeignPlate ? (
                                        <ForeignPlate />
                                    ) : (
                                        <VehicleLookupData />
                                    )}
                                </Stack>
                            )}
                        </Stack>
                    </Box>

                    <Box
                        sx={{
                            flexShrink: 0,
                            p: 3,
                            borderTop: `1px solid ${color.grey300}`,
                            bgcolor: color.white,
                        }}
                    >
                        <Stack direction="row" justifyContent={'space-between'} alignItems="center">
                            <Button
                                variant={'cancel'}
                                onClick={handleClose}
                                disabled={isLoadingLookUp}
                                sx={{ minWidth: '200px' }}
                            >
                                Cancel
                            </Button>
                            {!isShowLookup ? (
                                <Button
                                    variant={'contained'}
                                    disabled={!vrn}
                                    onClick={() => {
                                        fetchVRN();
                                    }}
                                    sx={{ minWidth: '200px' }}
                                >
                                    Lookup
                                </Button>
                            ) : (
                                <LoadingButton
                                    sx={{
                                        minWidth: '200px',
                                        justifyContent: props.updatingPlate ? 'space-between' : 'center',
                                    }}
                                    loading={props.updatingPlate}
                                    loadingPosition="center"
                                    variant="contained"
                                    disabled={isLoadingLookUp}
                                    startIcon={props.updatingPlate ? <></> : undefined}
                                    onClick={() => {
                                        handleUpdateChange();
                                    }}
                                >
                                    Update change
                                </LoadingButton>
                            )}
                        </Stack>
                    </Box>
                </Box>
            }
        />
    );
}
interface CustomTextFieldProps {
    style?: SxProps<Theme>;
}

export const CustomTextField = styled(TextField)<CustomTextFieldProps>(({ style, error }) => ({
    '& .MuiInputBase-root': {
        backgroundColor: '#FFFF00',
    },

    '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
        border: error ? `1px solid ${color.error}` : '1px solid #000000',
    },

    '& .MuiInputBase-input': {
        textAlign: 'center',
        letterSpacing: '4px',
        fontSize: style?.fontSize ? style?.fontSize : '32px',
        fontWeight: 500,
        fontFamily: 'Roboto',
        textTransform: 'uppercase',
    },

    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
        border: error ? `1px solid ${color.error}` : '1px solid #000000',
    },

    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
        border: error ? `1px solid ${color.error}` : '1px solid #000000',
    },
}));

type CropPlateProps = {
    photo: CameraPhotoVehicles;
    isOverviewPhoto?: boolean;
};

const CropPlate = (props: CropPlateProps) => {
    const blob = props.photo.CameraPhoto?.BlobName;
    const { image, loading, isError } = useImageDimensions(blob);
    console.log(`image:`, image?.width, image?.height);

    function calculateRectangleDimensions(x1, y1, x2, y2, x3, y3, x4, y4) {
        const width = Math.abs(x2 - x1);
        const height = Math.abs(y3 - y1);

        return { width, height };
    }

    const { X1, X2, X3, X4, Y1, Y2, Y3, Y4 } = props.photo;

    const plate = calculateRectangleDimensions(X1, Y1, X2, Y2, X3, Y3, X4, Y4);
    const containerWidth = props.isOverviewPhoto ? 180 : 200;
    const height = 115;
    const { previewWidth, previewHeight } = adjustPreviewSize(plate.width, plate.height, containerWidth, height);

    return loading ? (
        <Stack sx={{ flex: 1, justifyContent: 'center', alignItems: 'center', width: containerWidth, height: height }}>
            <CircularProgress size={20} sx={{ margin: 'auto' }} />
        </Stack>
    ) : (
        <Stage width={containerWidth} height={height}>
            <Layer>
                <Rect width={containerWidth} height={height} fill="white" />
                <KonvaImage
                    x={(containerWidth! - previewWidth) / 2}
                    y={(height! - previewHeight) / 2}
                    width={previewWidth}
                    height={previewHeight}
                    image={image}
                    crop={{
                        x: X1,
                        y: Y1,
                        width: plate.width,
                        height: plate.height,
                    }}
                />
            </Layer>
        </Stage>
    );
};
